import React, { useState, useEffect } from "react";
import { FaTimesCircle, FaRedo, FaExclamationCircle, FaDesktop, FaExternalLinkAlt, FaHistory, FaBullhorn, FaMapMarkerAlt, FaVideo, FaSearch, FaPlay, FaCheckCircle } from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import ReporterRaiseVoiceTabs from "./ReporterRaiseVoiceTabs";

const ReporterGetRejectVoice = () => {
  const navigate = useNavigate();
  const [rejectedVoices, setRejectedVoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmVoiceId, setConfirmVoiceId] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const fetchRejectedVoices = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reporter/get/rejected/ryv/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Response get rejected voices:", response);
        setRejectedVoices(Array.isArray(response.data.data?.proofs) ? response.data.data.proofs : []);
        setError(null);
      } catch (err) {
        setError("Failed to load rejected voices");
        toast.error("Failed to load rejected voices");
      } finally {
        setLoading(false);
      }
    };
    fetchRejectedVoices();
  }, []);

  const handleDeleteVoice = async (adId) => {
    try {
      setSubmitLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/update/proof/${adId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        toast.success("Ready for re-upload!");
        setTimeout(() => {
          navigate("/reporter/raisevoice/accepted");
        }, 1200);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset voice");
    } finally {
      setSubmitLoading(false);
    }
  };

  const totalPages = Math.ceil(rejectedVoices.length / rowsPerPage);
  const paginatedVoices = rejectedVoices.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-rose-100 border-t-rose-600 rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-rose-50/30 py-2 sm:py-6 px-2 sm:px-6">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Modern Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-6"
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-rose-700 via-pink-700 to-rose-900 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 text-white">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-rose-400/10 rounded-full blur-xl -ml-5 -mb-5"></div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="p-1.5 bg-white/20 backdrop-blur-md rounded-lg shadow-inner">
                  <FaExclamationCircle className="text-lg sm:text-xl text-rose-200" />
                </div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight leading-none uppercase italic">
                  Review Required
                </h1>
              </div>
              <p className="text-rose-100 text-[10px] sm:text-sm font-medium max-w-xl opacity-80">
                You have <span className="font-black text-white px-1.5 py-0.5 bg-white/10 rounded-md">{rejectedVoices.length}</span> submissions that were returned for revisions. Please check the feedback and re-submit.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <ReporterRaiseVoiceTabs />

      {error && (
        <div className="max-w-7xl mx-auto bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-3">
          <FaTimesCircle className="shrink-0" />
          <p className="font-medium text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="max-w-7xl mx-auto">
          {rejectedVoices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paginatedVoices.map((voice, idx) => (
                <motion.div
                  key={voice._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col sm:flex-row overflow-hidden group border-l-4 border-l-rose-500 min-h-[140px]"
                >
                  <div className="p-4 flex-1 flex flex-col min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-black text-rose-600 uppercase tracking-widest bg-rose-50 w-fit px-2 py-0.5 rounded-full border border-rose-100">Feedback Detail</span>
                        <h3 className="text-slate-900 font-extrabold text-sm sm:text-base leading-tight mt-1 truncate">
                          RYV ID: {voice.adId || "Not found"}
                        </h3>
                      </div>
                      <span className="text-[8px] font-black text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100 shrink-0">
                        {new Date(voice.submittedAt).toLocaleDateString('en-GB')}
                      </span>
                    </div>

                    <div className="mt-3 p-2.5 bg-rose-50/50 rounded-xl border border-rose-100/50 relative">
                      <p className="text-rose-900 text-[10px] font-bold italic line-clamp-2 leading-relaxed">
                        &quot;{voice.rejectionNote || "No specific feedback provided. Please verify all requirements before resubmitting."}&quot;
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-3 text-[9px] font-bold text-slate-400 uppercase">
                      <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100"><FaDesktop className="text-slate-400 shrink-0" /> {voice.platform}</span>
                      {voice.videoLink && (
                        <a href={voice.videoLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                          <FaPlay size={8} /> Original Proof
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-50 shrink-0 flex sm:flex-col items-center justify-center p-4 gap-3 border-t sm:border-t-0 sm:border-l border-slate-100 group-hover:bg-rose-50 transition-colors">
                    <button
                      onClick={() => {
                        setConfirmVoiceId(voice.adId);
                        setShowConfirmModal(true);
                        setIsChecked(false);
                      }}
                      className="w-full sm:w-28 flex items-center justify-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-700 active:scale-95 transition-all font-black text-[10px] uppercase tracking-wider"
                    >
                      <FaRedo className="shrink-0" /> Re-Upload
                    </button>
                    <p className="hidden sm:block text-[8px] font-black text-slate-400 uppercase tracking-tighter text-center leading-none">Discard &<br />Start Over</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center shadow-xl shadow-slate-200/20"
            >
              <div className="bg-emerald-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-emerald-400 text-4xl" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">Zero Rejections</h3>
              <p className="text-slate-500 max-w-xs mx-auto text-sm uppercase font-bold tracking-tight">Your work is looking great! All your submissions are either live or under review.</p>
            </motion.div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-10 mb-10">
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === 1 ? 'text-slate-200' : 'text-rose-600 hover:bg-rose-50 active:scale-90'}`}
                >
                  ←
                </button>
                <div className="flex items-center px-4 gap-1 font-black text-slate-800 text-[10px] uppercase tracking-widest">
                  <span>Page</span>
                  <span className="w-8 h-8 flex items-center justify-center bg-rose-600 text-white rounded-lg text-xs font-black">{currentPage}</span>
                  <span className="px-1 text-slate-400">of</span>
                  <span>{totalPages}</span>
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === totalPages ? 'text-slate-200' : 'text-rose-600 hover:bg-rose-50 active:scale-90'}`}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm Reupload Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !submitLoading && setShowConfirmModal(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100"
            >
              <div className="bg-gradient-to-r from-rose-600 to-rose-700 p-6 text-white text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md">
                  <FaRedo className="text-2xl" />
                </div>
                <h2 className="text-xl font-black uppercase tracking-tight italic">Reset Submission?</h2>
              </div>

              <div className="p-8">
                <p className="text-slate-500 font-bold text-sm text-center leading-relaxed">
                  Resetting will delete your previous proof and return this item to <span className="text-emerald-600">&quot;Accepted Assignments&quot;</span> for a fresh upload.
                </p>

                <div className="mt-8 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                      className="w-5 h-5 rounded-lg border-2 border-slate-300 text-rose-600 focus:ring-rose-500 transition-all checked:bg-rose-600"
                    />
                    <span className="text-xs font-black text-slate-700 uppercase tracking-tighter group-hover:text-rose-600 transition-colors">
                      Confirm Re-upload Request
                    </span>
                  </label>
                </div>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    disabled={submitLoading}
                    className="px-6 py-3 border-2 border-slate-100 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-400 hover:bg-slate-50 transition-colors"
                  >
                    Go Back
                  </button>
                  <button
                    disabled={!isChecked || submitLoading}
                    onClick={() => handleDeleteVoice(confirmVoiceId)}
                    className={`px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest text-white transition-all shadow-lg ${!isChecked || submitLoading ? 'bg-slate-200 shadow-none' : 'bg-rose-600 shadow-rose-200 hover:bg-rose-700 active:scale-95'}`}
                  >
                    {submitLoading ? 'Processing...' : 'Reset Now'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReporterGetRejectVoice;
